const SECRET_KEY_PATTERN = /(token|password|secret|authorization|credential|session|jwt|apiKey|accessKey|firebase|registrationId|uuid)/i;
const MAX_EVENTS = 160;
const MAX_STRING_LENGTH = 6000;

let events = [];
let installed = false;
let uninstallGlobalCapture = null;
let storeRef = null;
let originalConsole = {};

module.exports = {
    installGlobalCapture,
    uninstall,
    setStore,
    recordConsole,
    recordError,
    recordEvent,
    recordCordovaEvent,
    recordPushNotification,
    clearEvents,
    getEvents,
    getDiagnosticsSummary,
    buildSupportBundle,
    formatSupportBundleForBugReport,
    copySupportBundle,
    submitSupportBundle,
    getCordovaSnapshot,
    getNetworkSnapshot,
    redact,
    _resetForTests
};

function setStore(store) {

    storeRef = store;
}

function installGlobalCapture() {

    if (installed || !isEnabled() || typeof window === 'undefined') {
        return uninstall;
    }

    installed = true;
    originalConsole = {};

    ['log', 'info', 'warn', 'error'].forEach((level) => {
        const original = console[level];

        if (typeof original !== 'function') {
            return;
        }

        originalConsole[level] = original;
        console[level] = (...args) => {
            recordConsole(level, args);
            return original.apply(console, args);
        };
    });

    const onError = (event) => {
        recordError(event.error || {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
        }, {
            source: 'window.error'
        });
    };

    const onUnhandledRejection = (event) => {
        recordError(event.reason || {
            message: 'Unhandled rejection'
        }, {
            source: 'window.unhandledrejection'
        });
    };

    const onOnline = () => recordEvent({
        type: 'network',
        level: 'info',
        message: 'Browser reported online.',
        network: getNetworkSnapshot()
    });

    const onOffline = () => recordEvent({
        type: 'network',
        level: 'warn',
        message: 'Browser reported offline.',
        network: getNetworkSnapshot()
    });

    const onDeviceReady = () => recordCordovaEvent('deviceready');
    const onPause = () => recordCordovaEvent('pause');
    const onResume = () => recordCordovaEvent('resume');

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    if (typeof document !== 'undefined') {
        document.addEventListener('deviceready', onDeviceReady, false);
        document.addEventListener('pause', onPause, false);
        document.addEventListener('resume', onResume, false);
    }

    uninstallGlobalCapture = () => {
        Object.keys(originalConsole).forEach((level) => {
            console[level] = originalConsole[level];
        });

        window.removeEventListener('error', onError);
        window.removeEventListener('unhandledrejection', onUnhandledRejection);
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);

        if (typeof document !== 'undefined') {
            document.removeEventListener('deviceready', onDeviceReady, false);
            document.removeEventListener('pause', onPause, false);
            document.removeEventListener('resume', onResume, false);
        }

        originalConsole = {};
        installed = false;
        uninstallGlobalCapture = null;
    };

    recordEvent({
        type: 'diagnostics',
        level: 'info',
        message: 'Support diagnostics capture installed.',
        cordova: getCordovaSnapshot(),
        network: getNetworkSnapshot()
    });

    return uninstall;
}

function uninstall() {

    if (uninstallGlobalCapture) {
        uninstallGlobalCapture();
    }
}

function recordConsole(level, args = []) {

    recordEvent({
        type: 'console',
        level,
        args: args.map(formatValue)
    });
}

function recordError(error, meta = {}) {

    recordEvent({
        type: 'error',
        level: 'error',
        ...meta,
        error: formatError(error)
    });
}

function recordCordovaEvent(name, detail = {}) {

    recordEvent({
        type: 'cordova',
        level: 'info',
        name,
        detail: redact(detail),
        cordova: getCordovaSnapshot(),
        network: getNetworkSnapshot()
    });
}

function recordPushNotification(data = {}, meta = {}) {

    recordEvent({
        type: 'push-notification',
        level: 'info',
        meta,
        notification: redact({
            type: data.type,
            tap: data.tap,
            foreground: data.foreground,
            schoolId: data.schoolId,
            channelSid: data.channelSid,
            link: data.link,
            messageType: data.messageType,
            title: data.title,
            body: data.body
        }),
        network: getNetworkSnapshot()
    });
}

function recordEvent(event) {

    events = events.concat({
        timestamp: new Date().toISOString(),
        ...redact(event)
    }).slice(-MAX_EVENTS);
}

function clearEvents() {

    events = [];
}

function getEvents() {

    return events.slice();
}

function getDiagnosticsSummary() {

    const recentEvents = getEvents();

    return {
        installed,
        eventCount: recentEvents.length,
        cordova: getCordovaSnapshot(),
        network: getNetworkSnapshot(),
        recentEvents
    };
}

function buildSupportBundle({ notes = '', source = 'team-console', store = storeRef } = {}) {

    const {
        context
    } = require('app-context');

    const redux = store || context?.redux?.hooks;
    const state = safeValue(() => redux?.getState?.() || {}, {});
    const selectors = context?.selectors?.all || {};
    const currentUser = safeValue(() => selectors.getCurrentUser?.(state)?.user?.() || selectors.getCurrentUser?.(state), null);
    const currentSchool = safeValue(() => selectors.getCurrentSchool?.(state), null);

    return redact({
        schemaVersion: 2,
        source,
        generatedAt: new Date().toISOString(),
        notes,
        url: getLocationSnapshot(),
        browser: getBrowserSnapshot(),
        cordova: getCordovaSnapshot(),
        network: getNetworkSnapshot(),
        app: {
            appVersion: process.env.APP_VERSION,
            nodeEnv: process.env.NODE_ENV,
            debug: typeof __DEBUG__ !== 'undefined' ? __DEBUG__ : undefined,
            devConsoleEnabled: typeof __NEARPEER_DEV_CONSOLE_ENABLED__ !== 'undefined'
                ? __NEARPEER_DEV_CONSOLE_ENABLED__
                : undefined,
            teamConsoleEnabled: typeof __NEARPEER_TEAM_CONSOLE_ENABLED__ !== 'undefined'
                ? __NEARPEER_TEAM_CONSOLE_ENABLED__
                : undefined
        },
        user: compactObject({
            id: currentUser?.id,
            email: currentUser?.email,
            roleId: currentUser?.roleId,
            schoolId: currentUser?.schoolId
        }),
        school: compactObject({
            id: currentSchool?.id || currentUser?.schoolId,
            name: currentSchool?.name
        }),
        redux: {
            stateKeys: Object.keys(state || {}).sort(),
            selected: selectStateForSupport(state)
        },
        diagnostics: getDiagnosticsSummary()
    });
}

function formatSupportBundleForBugReport({ notes = '', bundle }) {

    return [
        notes ? `Team member notes:\n${notes}` : 'Team member notes: none provided.',
        '',
        '--- Nearpeer Support Bundle ---',
        JSON.stringify(bundle, null, 2)
    ].join('\n');
}

async function copySupportBundle({ notes = '', source = 'team-console', store = storeRef } = {}) {

    const bundle = buildSupportBundle({
        notes,
        source,
        store
    });
    const text = formatSupportBundleForBugReport({
        notes,
        bundle
    });

    await copyText(text);

    recordEvent({
        type: 'support-bundle',
        level: 'info',
        message: 'Support bundle copied.',
        source
    });

    return {
        bundle,
        text
    };
}

async function submitSupportBundle({ notes = '', source = 'team-console', store = storeRef } = {}) {

    const {
        context
    } = require('app-context');

    const redux = store || context?.redux?.hooks;
    const state = safeValue(() => redux?.getState?.() || {}, {});
    const selectors = context?.selectors?.all || {};
    const api = context?.api?.nearpeer;
    const actions = context?.actions;
    const currentUser = safeValue(() => selectors.getCurrentUser?.(state)?.user?.() || selectors.getCurrentUser?.(state), null);
    const currentSchool = safeValue(() => selectors.getCurrentSchool?.(state), null);
    const bundle = buildSupportBundle({
        notes,
        source,
        store: redux
    });

    const payload = {
        bugType: 'Team Console Support Bundle',
        email: currentUser?.email,
        bugDetails: {
            description: formatSupportBundleForBugReport({
                notes,
                bundle
            }),
            suggestedInterestCategory: '',
            deviceType: bundle.cordova.isCordova ? bundle.cordova.platform || 'Cordova' : bundle.browser.deviceType || '',
            browserType: bundle.cordova.isCordova ? `Cordova ${bundle.cordova.version || ''}`.trim() : bundle.browser.userAgent || '',
            role: currentUser?.roleId ? String(currentUser.roleId) : '',
            schoolId: currentSchool?.id || currentUser?.schoolId || null,
            schoolName: currentSchool?.name || ''
        }
    };

    if (!api?.appFeedback?.submitBugReport) {
        throw new Error('Support diagnostics submission requires api.nearpeer.appFeedback.submitBugReport.');
    }

    await api.appFeedback.submitBugReport(payload);

    if (redux?.dispatch && actions?.alerts?.notification?.push) {
        redux.dispatch(actions.alerts.notification.push({
            message: 'Support bundle sent to the dev team.'
        }));
    }

    recordEvent({
        type: 'support-bundle',
        level: 'info',
        message: 'Support bundle submitted.',
        source
    });

    return {
        payload,
        bundle
    };
}

function getLocationSnapshot() {

    if (typeof window === 'undefined' || !window.location) {
        return {};
    }

    return {
        href: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash
    };
}

function getBrowserSnapshot() {

    if (typeof navigator === 'undefined') {
        return {};
    }

    return {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        onLine: navigator.onLine,
        deviceType: inferDeviceType()
    };
}

function getCordovaSnapshot() {

    if (typeof window === 'undefined') {
        return {
            isCordova: false
        };
    }

    const device = window.device || {};
    const connection = getNetworkSnapshot();

    return compactObject({
        isCordova: !!window.cordova,
        platform: device.platform,
        version: device.version,
        model: device.model,
        manufacturer: device.manufacturer,
        isVirtual: device.isVirtual,
        serial: device.serial,
        cordovaVersion: window.cordova?.version,
        connectionType: connection.type
    });
}

function getNetworkSnapshot() {

    const connection = typeof navigator !== 'undefined'
        ? navigator.connection || navigator.mozConnection || navigator.webkitConnection
        : null;

    return compactObject({
        onLine: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        type: connection?.type,
        effectiveType: connection?.effectiveType,
        downlink: connection?.downlink,
        rtt: connection?.rtt,
        saveData: connection?.saveData
    });
}

function inferDeviceType() {

    if (typeof navigator === 'undefined') {
        return '';
    }

    const userAgent = navigator.userAgent || '';

    if (/Mobile|Android|iPhone|iPad|iPod/i.test(userAgent)) {
        return 'Mobile';
    }

    return 'Desktop';
}

function selectStateForSupport(state = {}) {

    return redact({
        auth: state.auth,
        signup: state.signup,
        app: {
            mainAppLoading: state.app?.mainAppLoading,
            schoolSwitchingState: state.app?.schoolSwitchingState,
            historyPreviousPathname: state.app?.historyPreviousPathname,
            inBackground: state.app?.inBackground
        },
        router: state.router,
        dataFetching: {
            loading: state.dataFetching?.loading,
            indexKeys: Object.keys(state.dataFetching?.index || {}).sort()
        }
    });
}

async function copyText(text) {

    if (typeof window !== 'undefined' && window.cordova?.plugins?.clipboard?.copy) {
        return new Promise((resolve, reject) => {
            window.cordova.plugins.clipboard.copy(text, resolve, reject);
        });
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(text);
    }

    if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea');

        textarea.value = text;
        textarea.setAttribute('readonly', 'readonly');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';

        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return undefined;
    }

    throw new Error('Clipboard is not available in this environment.');
}

function formatValue(value) {

    if (value instanceof Error) {
        return formatError(value);
    }

    if (typeof value === 'string') {
        return truncate(redactString(value));
    }

    return redact(value);
}

function formatError(error) {

    if (error instanceof Error) {
        return {
            name: error.name,
            message: error.message,
            stack: truncate(redactString(error.stack || ''))
        };
    }

    if (typeof error === 'string') {
        return {
            message: truncate(redactString(error))
        };
    }

    return redact(error);
}

function redact(value, key = '') {

    if (typeof value === 'string') {
        return SECRET_KEY_PATTERN.test(key)
            ? '[redacted]'
            : truncate(redactString(value));
    }

    if (Array.isArray(value)) {
        return value.map((item) => redact(item, key));
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    return Object.keys(value).reduce((collector, nextKey) => {

        const nextValue = value[nextKey];

        if (SECRET_KEY_PATTERN.test(nextKey)) {
            collector[nextKey] = '[redacted]';
            return collector;
        }

        collector[nextKey] = redact(nextValue, nextKey);
        return collector;
    }, {});
}

function redactString(value) {

    return String(value)
        .replace(/Bearer\s+[A-Za-z0-9._-]+/g, 'Bearer [redacted]')
        .replace(/(token|password|secret|authorization|credential|session|firebase|registrationId)=([^\s&]+)/gi, '$1=[redacted]');
}

function truncate(value) {

    if (typeof value !== 'string' || value.length <= MAX_STRING_LENGTH) {
        return value;
    }

    return `${value.slice(0, MAX_STRING_LENGTH)}…[truncated]`;
}

function compactObject(value) {

    return Object.keys(value || {}).reduce((collector, key) => {

        if (typeof value[key] !== 'undefined' && value[key] !== null && value[key] !== '') {
            collector[key] = value[key];
        }

        return collector;
    }, {});
}

function safeValue(fn, fallback) {

    try {
        return fn();
    }
    catch (err) {
        return fallback;
    }
}

function isEnabled() {

    const devConsoleEnabled = typeof __NEARPEER_DEV_CONSOLE_ENABLED__ !== 'undefined'
        ? __NEARPEER_DEV_CONSOLE_ENABLED__
        : process.env.NEARPEER_DEV_CONSOLE_ENABLED === 'true';
    const teamConsoleEnabled = typeof __NEARPEER_TEAM_CONSOLE_ENABLED__ !== 'undefined'
        ? __NEARPEER_TEAM_CONSOLE_ENABLED__
        : process.env.NEARPEER_TEAM_CONSOLE_ENABLED === 'true';

    return !!devConsoleEnabled || !!teamConsoleEnabled;
}

function _resetForTests() {

    uninstall();
    events = [];
    installed = false;
    uninstallGlobalCapture = null;
    storeRef = null;
    originalConsole = {};
}
